// A clock that stands still while the phone app is in the background.
//
// Inside the phone app the page stops when the app is backgrounded: Android
// freezes it, iOS suspends it. Every timer and every "how long has this taken"
// measurement keeps counting through that pause, so when the user comes back
// work that was going fine looks as if it has been stuck for minutes. The video
// compressor was the case that proved it (client report, 2026-10-19, Galaxy
// S23): it judges its own speed against the clock, so a pause read as a slow
// phone, and it gave up and sent the full-size original.
//
// So anything that asks "has this taken too long" reads foregroundNow() and
// arms setForegroundTimeout(). Anything that reports how long the USER waited
// keeps the wall clock: that wait was real.
//
// ONLY IN THE APP (backgroundStopsClock). A browser tab that is hidden keeps
// running, only slower, and there the old answer was the right one: a slow
// encode gives up and the original uploads while the user is in another tab.
// Stopping the clock there would leave the upload waiting until they came back.
// In the app the page is about to stop outright, and the work after the encode
// is page work too, until the phone's own uploader takes the file.
//
// Background is measured from `visibilitychange`, which the page gets when the
// app goes to the background and when it comes back, on every platform; only
// what the clocks do with it differs. Durations are on the monotonic clock,
// which on some phones does not advance while the phone sleeps, so a hidden
// figure here can be shorter than the wall-clock background time.

import { isNativeApp } from "./mobiloud_detection";

const monotonicNow = () =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

let hiddenTotalMs = 0;
let hiddenSince = null;
let listening = false;

const pageIsHidden = () =>
  typeof document !== "undefined" && document.visibilityState === "hidden";

function handleVisibilityChange() {
  const now = monotonicNow();
  if (pageIsHidden()) {
    if (hiddenSince === null) hiddenSince = now;
    return;
  }
  if (hiddenSince !== null) {
    hiddenTotalMs += now - hiddenSince;
    hiddenSince = null;
  }
}

// Started on first use rather than at import, so a page that never measures
// anything never listens. Only differences between two readings mean anything,
// so time hidden before the first reading is correctly not counted.
function startListening() {
  if (listening || typeof document === "undefined") return;
  listening = true;
  if (pageIsHidden()) hiddenSince = monotonicNow();
  document.addEventListener("visibilitychange", handleVisibilityChange);
}

// All the time spent hidden since listening began, including a stretch still in
// progress. Reads the clock only while the page is hidden.
function hiddenSoFar(now = null) {
  if (hiddenSince === null) return hiddenTotalMs;
  return hiddenTotalMs + (now ?? monotonicNow()) - hiddenSince;
}

/**
 * True where time in the background is left out of foregroundNow() and
 * setForegroundTimeout(): inside the phone app. See the module comment.
 * @returns {boolean}
 */
export function backgroundStopsClock() {
  return isNativeApp();
}

/**
 * Monotonic milliseconds. Inside the app they do not advance while the page is
 * hidden; in a browser this is the plain monotonic clock.
 * @returns {number}
 */
export function foregroundNow() {
  startListening();
  const now = monotonicNow();
  return backgroundStopsClock() ? now - hiddenSoFar(now) : now;
}

/**
 * Starts counting background time, on every platform. The returned function
 * says how many whole milliseconds the page has spent hidden since this was
 * called. Neither half reads the clock while the page is in front, so starting
 * one costs nothing.
 * @returns {() => number}
 */
export function startHiddenStopwatch() {
  startListening();
  const hiddenAtStart = hiddenSoFar();
  return () => Math.max(0, Math.round(hiddenSoFar() - hiddenAtStart));
}

/**
 * setTimeout that, inside the app, only counts time the page is in front of the
 * user. A plain timer that falls due while the app is frozen fires the moment
 * it comes back, however little foreground time has passed. This one takes off
 * whatever part of each wait was spent in the background and waits that part
 * out again. In a browser it behaves as a plain setTimeout.
 *
 * Each wait is measured as the longer of the timer's own delay (a timer never
 * fires early) and the clock's reading, so it fires on time even where the
 * clock does not move in step with timers.
 *
 * @param {() => void} callback
 * @param {number} ms
 * @returns {() => void} cancels the timer; safe to call more than once.
 */
export function setForegroundTimeout(callback, ms) {
  let owedMs = ms;
  let timer = null;
  let done = false;
  const wait = () => {
    const delay = owedMs;
    const armedAt = monotonicNow();
    const hiddenMs = startHiddenStopwatch();
    timer = setTimeout(() => {
      if (done) return;
      const waitedMs = Math.max(delay, monotonicNow() - armedAt);
      const backgroundMs = backgroundStopsClock() ? hiddenMs() : 0;
      owedMs -= Math.max(0, waitedMs - backgroundMs);
      if (owedMs > 0) {
        wait();
        return;
      }
      done = true;
      callback();
    }, delay);
  };
  wait();
  return () => {
    done = true;
    clearTimeout(timer);
  };
}

/** Test-only: forget all recorded background time and stop listening. */
export function __resetForegroundClockForTests() {
  if (listening && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  }
  hiddenTotalMs = 0;
  hiddenSince = null;
  listening = false;
}
